// src/pages/SubcontractorWHS.tsx — Subcontractor WHS Management
// WHS compliance gate for all subcontractors: pre-qual, insurance,
// SWMS review, worker induction tracking, and safety scorecard.
//
// Follows frontend-skill.md conventions:
//   - Functional components + hooks only
//   - Tailwind utility classes only, indigo-600 primary
//   - Page wrapper: <div className="p-6 space-y-6">

import { useState, useMemo } from 'react'
import { useAuthStore } from '../stores/auth.store'
import { MOCK_WORKER_INDUCTIONS, MOCK_WHS_INSPECTIONS, MOCK_PERMITS } from '../utils/mockDataWHS'
import { MOCK_JOBS } from '../utils/mockData'
import { formatDate } from '../utils/formatters'
import { useQuery } from '@tanstack/react-query'
import { api } from '../utils/api'
import {
  Search, Plus, X, CheckCircle, AlertTriangle, Clock,
  ChevronRight, ArrowLeft, Shield, FileText, Users,
  Building2, Phone, Mail, Star, BarChart2, Upload,
  AlertCircle, Ban, ExternalLink, UserCheck, ClipboardList,
} from 'lucide-react'

// ─────────────────────────────────────────────────────────────────
// LOCAL TYPES
// ─────────────────────────────────────────────────────────────────

type SubStatus   = 'APPROVED' | 'PENDING' | 'SUSPENDED' | 'BLACKLISTED'
type PrequalStatus = 'NOT_STARTED' | 'IN_PROGRESS' | 'APPROVED' | 'EXPIRED'
type DocStatus   = 'CURRENT' | 'EXPIRING' | 'EXPIRED' | 'MISSING'
type SwmsStatus  = 'SUBMITTED' | 'UNDER_REVIEW' | 'APPROVED' | 'REJECTED' | 'SUPERSEDED'
type TradeType   = 'ELECTRICAL' | 'CIVIL' | 'TRAFFIC_CONTROL' | 'LABOUR_HIRE' | 'SPECIALIST'

interface ComplianceDoc {
  label:       string
  docNumber?:  string
  insurer?:    string
  expiry?:     string
  amount?:     string           // for PL insurance
  present:     boolean
}

interface SubSWMS {
  id:              string
  title:           string
  revision:        string
  submittedAt:     string
  submittedByName: string
  status:          SwmsStatus
  reviewedByName?: string
  reviewedAt?:     string
  reviewNotes?:    string
  linkedJobId?:    string
}

interface SafetyScore {
  incidents:        number
  openIssues:       number
  swmsCompliance:   number    // pct
  inductionRate:    number    // pct
  avgRating:        number    // 1-5
}

interface Subcontractor {
  id:             string
  companyName:    string
  abn:            string
  trade:          TradeType
  contactName:    string
  contactPhone:   string
  contactEmail:   string
  status:         SubStatus
  prequalStatus:  PrequalStatus
  prequalExpiry?: string
  onSiteJobs:     string[]    // job IDs currently active on
  // Compliance documents
  publicLiability:    ComplianceDoc
  workersComp:        ComplianceDoc
  electricalLicence:  ComplianceDoc
  whsManagementSystem: boolean
  // WHS history
  swms:           SubSWMS[]
  safetyScore:    SafetyScore
  notes?:         string
}

// ─────────────────────────────────────────────────────────────────
// MOCK DATA (inline — no extra file needed)
// ─────────────────────────────────────────────────────────────────

const MOCK_SUBCONTRACTORS: Subcontractor[] = [
  {
    id: 'sub1',
    companyName: 'Thomson Electrical Pty Ltd',
    abn: '61 123 456 789',
    trade: 'ELECTRICAL',
    contactName: 'Brad Thomson',
    contactPhone: '0455 123 456',
    contactEmail: 'brad@thomsonelectrical.com.au',
    status: 'APPROVED',
    prequalStatus: 'APPROVED',
    prequalExpiry: '2027-03-01',
    onSiteJobs: ['j1'],
    publicLiability: {
      label: 'Public Liability Insurance',
      insurer: 'CGU Insurance',
      docNumber: 'CGU-PL-2024-88441',
      expiry: '2027-01-15',
      amount: '$20,000,000',
      present: true,
    },
    workersComp: {
      label: 'Workers Compensation',
      insurer: 'WorkCover QLD',
      docNumber: 'WC-QLD-2024-551234',
      expiry: '2026-09-30',
      present: true,
    },
    electricalLicence: {
      label: 'Electrical Contractor Licence',
      docNumber: 'EC-QLD-77432',
      expiry: '2027-02-28',
      present: true,
    },
    whsManagementSystem: true,
    swms: [
      {
        id: 'ss1', title: 'Thomson Elec — Underground Cable Installation SWMS',
        revision: 'Rev 2', submittedAt: '2026-06-14T09:00:00Z',
        submittedByName: 'Brad Thomson', status: 'APPROVED',
        reviewedByName: 'Sarah Kim (WHSO)', reviewedAt: '2026-06-14T14:30:00Z',
        reviewNotes: 'Approved. Hand-dig exclusion zone updated to 500mm per site instruction.',
        linkedJobId: 'j1',
      },
    ],
    safetyScore: { incidents: 0, openIssues: 0, swmsCompliance: 100, inductionRate: 100, avgRating: 4.5 },
  },
  {
    id: 'sub2',
    companyName: 'VD Civil Pty Ltd',
    abn: '72 987 654 321',
    trade: 'CIVIL',
    contactName: 'Pham Van Duc',
    contactPhone: '0466 789 012',
    contactEmail: 'duc@vdcivil.com.au',
    status: 'APPROVED',
    prequalStatus: 'APPROVED',
    prequalExpiry: '2026-11-15',
    onSiteJobs: ['j1'],
    publicLiability: {
      label: 'Public Liability Insurance',
      insurer: 'QBE Insurance',
      docNumber: 'QBE-PL-2024-33129',
      expiry: '2026-08-01',
      amount: '$10,000,000',
      present: true,
    },
    workersComp: {
      label: 'Workers Compensation',
      insurer: 'WorkCover QLD',
      docNumber: 'WC-QLD-2024-662341',
      expiry: '2026-07-15',   // expiring in < 30 days — amber
      present: true,
    },
    electricalLicence: {
      label: 'Electrical Contractor Licence',
      present: false,         // civil trade — not required
    },
    whsManagementSystem: false,
    swms: [
      {
        id: 'ss2', title: 'VD Civil — Excavation & Trenching SWMS',
        revision: 'Rev 1', submittedAt: '2026-06-18T11:00:00Z',
        submittedByName: 'Van Duc Pham', status: 'UNDER_REVIEW',
        linkedJobId: 'j1',
      },
    ],
    safetyScore: { incidents: 1, openIssues: 1, swmsCompliance: 75, inductionRate: 50, avgRating: 3.0 },
    notes: 'Failed company induction on first attempt (Pham Van Duc — 60%). Re-induction scheduled.',
  },
  {
    id: 'sub3',
    companyName: 'Williams Labour Hire',
    abn: '55 234 567 890',
    trade: 'LABOUR_HIRE',
    contactName: 'Jamal Williams',
    contactPhone: '0477 334 556',
    contactEmail: 'jamal@williamslabour.com.au',
    status: 'PENDING',
    prequalStatus: 'IN_PROGRESS',
    onSiteJobs: [],
    publicLiability: {
      label: 'Public Liability Insurance',
      present: false,   // not yet submitted
    },
    workersComp: {
      label: 'Workers Compensation',
      present: false,
    },
    electricalLicence: {
      label: 'Electrical Contractor Licence',
      present: false,   // not applicable
    },
    whsManagementSystem: false,
    swms: [],
    safetyScore: { incidents: 0, openIssues: 0, swmsCompliance: 0, inductionRate: 0, avgRating: 0 },
    notes: 'Pre-qualification in progress. Awaiting insurance certificates and WHS questionnaire.',
  },
  {
    id: 'sub4',
    companyName: 'Alpha Traffic Control Pty Ltd',
    abn: '83 456 789 012',
    trade: 'TRAFFIC_CONTROL',
    contactName: 'Sandra Moretti',
    contactPhone: '0400 887 223',
    contactEmail: 'sandra@alphatraffic.com.au',
    status: 'APPROVED',
    prequalStatus: 'APPROVED',
    prequalExpiry: '2027-06-01',
    onSiteJobs: ['j1', 'j3'],
    publicLiability: {
      label: 'Public Liability Insurance',
      insurer: 'Allianz Australia',
      docNumber: 'ALZ-PL-2025-10412',
      expiry: '2027-04-30',
      amount: '$20,000,000',
      present: true,
    },
    workersComp: {
      label: 'Workers Compensation',
      insurer: 'WorkCover QLD',
      docNumber: 'WC-QLD-2025-774421',
      expiry: '2026-10-31',
      present: true,
    },
    electricalLicence: {
      label: 'Electrical Contractor Licence',
      present: false,   // traffic control — not required
    },
    whsManagementSystem: true,
    swms: [
      {
        id: 'ss3', title: 'Alpha TC — Traffic Management in Works Zones SWMS',
        revision: 'Rev 3', submittedAt: '2026-06-10T08:30:00Z',
        submittedByName: 'Sandra Moretti', status: 'APPROVED',
        reviewedByName: 'Sarah Kim (WHSO)', reviewedAt: '2026-06-10T13:00:00Z',
        linkedJobId: 'j1',
      },
      {
        id: 'ss4', title: 'Alpha TC — Night Works Traffic Management SWMS',
        revision: 'Rev 1', submittedAt: '2026-06-20T16:00:00Z',
        submittedByName: 'Sandra Moretti', status: 'SUBMITTED',
        linkedJobId: 'j3',
      },
    ],
    safetyScore: { incidents: 0, openIssues: 0, swmsCompliance: 100, inductionRate: 100, avgRating: 4.8 },
  },
]

// ─────────────────────────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────────────────────────

const DAYS = 1000 * 60 * 60 * 24

function daysUntil(dateStr?: string) {
  if (!dateStr) return null
  return Math.ceil((new Date(dateStr).getTime() - Date.now()) / DAYS)
}

function docStatus(doc: ComplianceDoc): DocStatus {
  if (!doc.present) return 'MISSING'
  if (!doc.expiry)  return 'CURRENT'
  const d = daysUntil(doc.expiry)
  if (d === null)   return 'CURRENT'
  if (d < 0)        return 'EXPIRED'
  if (d <= 30)      return 'EXPIRING'
  return 'CURRENT'
}

function overallCompliance(sub: Subcontractor): 'GREEN' | 'AMBER' | 'RED' {
  if (sub.status === 'SUSPENDED' || sub.status === 'BLACKLISTED') return 'RED'
  if (sub.status === 'PENDING') return 'AMBER'
  const criticalDocs = [sub.publicLiability, sub.workersComp]
  const anyExpired  = criticalDocs.some(d => docStatus(d) === 'EXPIRED' || docStatus(d) === 'MISSING')
  const anyExpiring = criticalDocs.some(d => docStatus(d) === 'EXPIRING')
  const pendingSwms = sub.swms.some(s => s.status === 'SUBMITTED' || s.status === 'UNDER_REVIEW')
  if (anyExpired) return 'RED'
  if (anyExpiring || pendingSwms || !sub.whsManagementSystem) return 'AMBER'
  return 'GREEN'
}

// ─────────────────────────────────────────────────────────────────
// META MAPS
// ─────────────────────────────────────────────────────────────────

const STATUS_META: Record<SubStatus, { label: string; bg: string; text: string }> = {
  APPROVED:    { label: 'Approved',    bg: 'bg-green-100',  text: 'text-green-700'  },
  PENDING:     { label: 'Pending',     bg: 'bg-amber-100',  text: 'text-amber-700'  },
  SUSPENDED:   { label: 'Suspended',   bg: 'bg-red-100',    text: 'text-red-700'    },
  BLACKLISTED: { label: 'Blacklisted', bg: 'bg-gray-900',   text: 'text-white'      },
}

const PREQUAL_META: Record<PrequalStatus, { label: string; bg: string; text: string }> = {
  NOT_STARTED:  { label: 'Not started',   bg: 'bg-slate-100',  text: 'text-slate-500'  },
  IN_PROGRESS:  { label: 'In progress',   bg: 'bg-blue-100',   text: 'text-blue-700'   },
  APPROVED:     { label: 'Pre-qual approved', bg: 'bg-green-100', text: 'text-green-700' },
  EXPIRED:      { label: 'Pre-qual expired',  bg: 'bg-red-100',   text: 'text-red-700'   },
}

const SWMS_STATUS_META: Record<SwmsStatus, { label: string; bg: string; text: string }> = {
  SUBMITTED:    { label: 'Submitted',    bg: 'bg-blue-100',   text: 'text-blue-700'   },
  UNDER_REVIEW: { label: 'Under review', bg: 'bg-purple-100', text: 'text-purple-700' },
  APPROVED:     { label: 'Approved',     bg: 'bg-green-100',  text: 'text-green-700'  },
  REJECTED:     { label: 'Rejected',     bg: 'bg-red-100',    text: 'text-red-700'    },
  SUPERSEDED:   { label: 'Superseded',   bg: 'bg-slate-100',  text: 'text-slate-500'  },
}

const TRADE_META: Record<TradeType, { label: string; bg: string; text: string; icon: string }> = {
  ELECTRICAL:      { label: 'Electrical',      bg: 'bg-yellow-50', text: 'text-yellow-700', icon: '⚡' },
  CIVIL:           { label: 'Civil',           bg: 'bg-orange-50', text: 'text-orange-700', icon: '🏗' },
  TRAFFIC_CONTROL: { label: 'Traffic control', bg: 'bg-teal-50',   text: 'text-teal-700',   icon: '🚦' },
  LABOUR_HIRE:     { label: 'Labour hire',     bg: 'bg-slate-100', text: 'text-slate-600',  icon: '👷' },
  SPECIALIST:      { label: 'Specialist',      bg: 'bg-indigo-50', text: 'text-indigo-700', icon: '🔧' },
}

const DOC_STATUS_META: Record<DocStatus, { icon: any; text: string; bg: string; label: string }> = {
  CURRENT:  { icon: CheckCircle, text: 'text-green-600',  bg: 'bg-green-50',  label: 'Current' },
  EXPIRING: { icon: Clock,       text: 'text-amber-600',  bg: 'bg-amber-50',  label: 'Expiring' },
  EXPIRED:  { icon: AlertCircle, text: 'text-red-600',    bg: 'bg-red-50',    label: 'Expired' },
  MISSING:  { icon: X,           text: 'text-slate-400',  bg: 'bg-slate-50',  label: 'Not provided' },
}

const COMPLIANCE_DOT: Record<'GREEN' | 'AMBER' | 'RED', string> = {
  GREEN: 'bg-green-500',
  AMBER: 'bg-amber-400',
  RED:   'bg-red-500',
}

// ─────────────────────────────────────────────────────────────────
// DOC ROW COMPONENT
// ─────────────────────────────────────────────────────────────────

function DocRow({ doc, required = true }: { doc: ComplianceDoc; required?: boolean }) {
  const ds  = docStatus(doc)
  const meta = DOC_STATUS_META[ds]
  const Icon = meta.icon
  const days = daysUntil(doc.expiry)

  if (!required && !doc.present) return (
    <div className="flex items-center justify-between py-2.5 border-b border-slate-100 last:border-0">
      <span className="text-sm text-slate-400">{doc.label}</span>
      <span className="text-xs text-slate-300 italic">Not applicable</span>
    </div>
  )

  return (
    <div className={`flex items-start justify-between py-2.5 border-b border-slate-100 last:border-0 ${!doc.present ? '' : ds !== 'CURRENT' ? `${meta.bg} rounded-lg px-2 -mx-2` : ''}`}>
      <div className="flex items-start gap-2 min-w-0">
        <Icon className={`w-4 h-4 mt-0.5 flex-shrink-0 ${meta.text}`} />
        <div className="min-w-0">
          <p className="text-sm text-slate-700">{doc.label}</p>
          {doc.insurer && <p className="text-xs text-slate-400">{doc.insurer}{doc.docNumber ? ` · ${doc.docNumber}` : ''}</p>}
          {doc.amount  && <p className="text-xs text-slate-400">Cover: {doc.amount}</p>}
        </div>
      </div>
      <div className="text-right flex-shrink-0 ml-3">
        {!doc.present
          ? <span className="text-xs text-slate-400 italic">Not submitted</span>
          : doc.expiry
            ? <>
                <span className={`text-xs font-medium ${meta.text}`}>
                  {ds === 'EXPIRED' ? '⚠ Expired ' : ds === 'EXPIRING' ? `⚠ Exp in ${days}d` : 'Exp '}
                </span>
                <span className="text-xs text-slate-500">{formatDate(doc.expiry)}</span>
              </>
            : <span className="text-xs text-green-600">Current</span>
        }
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────
// REVIEW SWMS MODAL
// ─────────────────────────────────────────────────────────────────

function ReviewSWMSModal({ swms, subName, onClose, onDecision }: {
  swms:       SubSWMS
  subName:    string
  onClose:    () => void
  onDecision: (id: string, decision: 'APPROVED' | 'REJECTED', notes: string) => void
}) {
  const [notes, setNotes] = useState('')
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <div>
            <h2 className="font-semibold text-slate-900">Review SWMS</h2>
            <p className="text-xs text-slate-400 mt-0.5">{subName}</p>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-slate-100 rounded-lg">
            <X className="w-4 h-4 text-slate-400" />
          </button>
        </div>
        <div className="p-6 space-y-4">
          <div className="bg-slate-50 rounded-xl p-4 space-y-2">
            <p className="text-sm font-semibold text-slate-900">{swms.title}</p>
            <div className="flex items-center gap-4 text-xs text-slate-400">
              <span>{swms.revision}</span>
              <span>Submitted {formatDate(swms.submittedAt)}</span>
              <span>By {swms.submittedByName}</span>
            </div>
            {swms.linkedJobId && (
              <p className="text-xs text-indigo-600">
                {MOCK_JOBS.find(j => j.id === swms.linkedJobId)?.jobNumber}
              </p>
            )}
          </div>
          <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-3 text-xs text-blue-700">
            In production, the uploaded SWMS PDF opens here for review. Check: hazard identification, hierarchy of controls, emergency procedures, and worker competency requirements.
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">
              Review notes <span className="text-slate-400">(required for rejection)</span>
            </label>
            <textarea value={notes} onChange={e => setNotes(e.target.value)}
              placeholder="Add notes, conditions, or reasons for rejection..."
              className="input w-full resize-none h-24 text-sm" autoFocus />
          </div>
        </div>
        <div className="px-6 py-4 border-t bg-slate-50 flex gap-2">
          <button onClick={() => { onDecision(swms.id, 'REJECTED', notes); onClose() }}
            className="flex-1 py-2 text-sm font-medium text-red-700 bg-red-50 hover:bg-red-100 border border-red-200 rounded-lg transition-colors">
            Reject
          </button>
          <button onClick={() => { onDecision(swms.id, 'APPROVED', notes); onClose() }}
            className="flex-1 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-lg transition-colors">
            Approve SWMS
          </button>
        </div>
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────
// SUBCONTRACTOR DETAIL
// ─────────────────────────────────────────────────────────────────

function SubcontractorDetail({ sub: initial, onBack, onSwmsDecision }: {
  sub:           Subcontractor
  onBack:        () => void
  onSwmsDecision:(subId: string, swmsId: string, decision: 'APPROVED' | 'REJECTED', notes: string) => void
}) {
  const { isTier } = useAuthStore()
  const [sub, setSub]       = useState(initial)
  const [showNCModal, setShowNCModal] = useState(false)
  const [ncForm, setNcForm] = useState({
    noticeRef: `NCN-${new Date().getFullYear()}-001`,
    issueDate: new Date().toISOString().slice(0,10),
    nonComplianceType: 'SWMS_NOT_SUBMITTED',
    description: '', contractRef: (initial.onSiteJobs?.[0] ?? ''),
    obligationRef: '', rectificationRequired: '', rectificationDeadline: '',
    consequences: '', whsoName: 'Sarah Kim (WHSO)',
  })
  const [tab, setTab]       = useState<'overview' | 'swms' | 'workers' | 'score'>('overview')
  const [reviewingSwms, setReviewingSwms] = useState<SubSWMS | null>(null)

  const canReview  = isTier('B_PC')
  const compliance = overallCompliance(sub)
  const tradeMeta  = TRADE_META[sub.trade]
  const statusMeta = STATUS_META[sub.status]

  // Workers from this company found in induction records
  const subWorkers = MOCK_WORKER_INDUCTIONS.filter(
    w => w.workerType === 'SUBCONTRACTOR' && w.company === sub.companyName
  )

  // Permits currently active for this sub's workers
  const activePermits = MOCK_PERMITS.filter(p =>
    p.workers.some(w => subWorkers.some(sw => sw.workerId === w.employeeId || sw.workerName === w.name))
  )

  // Open issues from inspections where category matches this trade
  const openIssues = MOCK_WHS_INSPECTIONS.flatMap(i => i.issues).filter(i => i.status !== 'CLOSED')

  const handleSwmsDecision = (swmsId: string, decision: 'APPROVED' | 'REJECTED', notes: string) => {
    setSub(prev => ({
      ...prev,
      swms: prev.swms.map(s => s.id !== swmsId ? s : {
        ...s, status: decision, reviewedByName: 'Sarah Kim (WHSO)',
        reviewedAt: new Date().toISOString(), reviewNotes: notes || undefined,
      }),
    }))
    onSwmsDecision(sub.id, swmsId, decision, notes)
  }

  const pendingSwms = sub.swms.filter(s => s.status === 'SUBMITTED' || s.status === 'UNDER_REVIEW')

  return (
    <div className="p-6 space-y-5 max-w-3xl">
      {/* Back */}
      <button onClick={onBack} className="flex items-center gap-2 text-sm text-slate-500 hover:text-slate-900 transition-colors">
        <ArrowLeft className="w-4 h-4" /> Back to subcontractors
      </button>
      <button onClick={() => setShowNCModal(true)} className="text-xs px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium">Non-compliance notice</button>

      {/* Header */}
      <div className="bg-white border border-slate-200 rounded-xl p-5">
        <div className="flex items-start gap-4">
          <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${tradeMeta.bg} text-2xl`}>
            {tradeMeta.icon}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <h1 className="text-lg font-bold text-slate-900">{sub.companyName}</h1>
              <span className={`text-xs font-medium px-2.5 py-0.5 rounded-full ${statusMeta.bg} ${statusMeta.text}`}>
                {statusMeta.label}
              </span>
              <span className={`flex items-center gap-1.5 text-xs font-medium px-2.5 py-0.5 rounded-full
                ${compliance === 'GREEN' ? 'bg-green-100 text-green-700' : compliance === 'AMBER' ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}`}>
                <span className={`w-1.5 h-1.5 rounded-full ${COMPLIANCE_DOT[compliance]}`} />
                WHS {compliance === 'GREEN' ? 'Compliant' : compliance === 'AMBER' ? 'Action needed' : 'Non-compliant'}
              </span>
            </div>
            <div className="flex items-center gap-4 text-xs text-slate-400 flex-wrap">
              <span>ABN {sub.abn}</span>
              <span className={`px-2 py-0.5 rounded-full ${tradeMeta.bg} ${tradeMeta.text}`}>{tradeMeta.label}</span>
              {sub.onSiteJobs.length > 0 && (
                <span className="text-indigo-600 font-medium">
                  Active on: {sub.onSiteJobs.map(id => MOCK_JOBS.find(j => j.id === id)?.jobNumber).join(', ')}
                </span>
              )}
            </div>
            <div className="flex items-center gap-4 mt-2 text-xs text-slate-500">
              <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{sub.contactPhone}</span>
              <span className="flex items-center gap-1"><Mail className="w-3 h-3" />{sub.contactEmail}</span>
              <span className="flex items-center gap-1"><Users className="w-3 h-3" />{sub.contactName}</span>
            </div>
          </div>
        </div>

        {/* Pre-qual strip */}
        <div className="mt-4 pt-4 border-t grid grid-cols-2 sm:grid-cols-4 gap-3 text-center text-xs">
          {[
            { label: 'Pre-qual', value: PREQUAL_META[sub.prequalStatus].label, color: sub.prequalStatus === 'APPROVED' ? 'text-green-600' : 'text-amber-600' },
            { label: 'WHS System', value: sub.whsManagementSystem ? 'In place' : 'Not confirmed', color: sub.whsManagementSystem ? 'text-green-600' : 'text-red-600' },
            { label: 'SWMS on file', value: `${sub.swms.filter(s => s.status === 'APPROVED').length} approved`, color: 'text-slate-700' },
            { label: 'Workers inducted', value: `${subWorkers.filter(w => w.status === 'PASSED').length}`, color: 'text-slate-700' },
          ].map(s => (
            <div key={s.label}>
              <p className="text-slate-400">{s.label}</p>
              <p className={`font-semibold mt-0.5 ${s.color}`}>{s.value}</p>
            </div>
          ))}
        </div>

        {/* Pending SWMS alert */}
        {pendingSwms.length > 0 && (
          <div className="mt-4 flex items-center gap-2 text-xs text-blue-700 bg-blue-50 rounded-lg px-3 py-2.5 border border-blue-200">
            <Clock className="w-3.5 h-3.5 flex-shrink-0" />
            {pendingSwms.length} SWMS awaiting WHSO review — workers cannot start until approved
            <button onClick={() => setTab('swms')} className="ml-auto underline underline-offset-2">Review now</button>
          </div>
        )}

        {/* Notes */}
        {sub.notes && (
          <div className="mt-3 text-xs text-amber-700 bg-amber-50 rounded-lg px-3 py-2 flex gap-2">
            <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
            {sub.notes}
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-slate-100 rounded-xl p-1">
        {[
          { id: 'overview', label: 'Compliance docs' },
          { id: 'swms',     label: `SWMS (${sub.swms.length})`, alert: pendingSwms.length > 0 },
          { id: 'workers',  label: `Workers (${subWorkers.length})` },
          { id: 'score',    label: 'Safety score' },
        ].map(t => (
          <button key={t.id} onClick={() => setTab(t.id as any)}
            className={`flex-1 text-xs font-medium py-2 rounded-lg transition-colors relative
              ${tab === t.id ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>
            {t.label}
            {t.alert && <span className="absolute top-1 right-2 w-1.5 h-1.5 bg-blue-500 rounded-full" />}
          </button>
        ))}
      </div>

      {/* Tab: Compliance docs */}
      {tab === 'overview' && (
        <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-1">
          <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">Insurance &amp; licences</p>
          <DocRow doc={sub.publicLiability} />
          <DocRow doc={sub.workersComp} />
          <DocRow doc={sub.electricalLicence} required={sub.trade === 'ELECTRICAL' || sub.trade === 'SPECIALIST'} />
          <div className="flex items-center justify-between py-2.5 border-b border-slate-100">
            <span className="text-sm text-slate-700">WHS Management System</span>
            {sub.whsManagementSystem
              ? <span className="flex items-center gap-1 text-xs text-green-700"><CheckCircle className="w-3.5 h-3.5" /> Confirmed</span>
              : <span className="flex items-center gap-1 text-xs text-red-600"><X className="w-3.5 h-3.5" /> Not confirmed</span>
            }
          </div>
          <div className="flex items-center justify-between py-2.5">
            <span className="text-sm text-slate-700">Pre-qualification</span>
            <div className="flex items-center gap-2">
              <span className={`text-xs px-2 py-0.5 rounded-full ${PREQUAL_META[sub.prequalStatus].bg} ${PREQUAL_META[sub.prequalStatus].text}`}>
                {PREQUAL_META[sub.prequalStatus].label}
              </span>
              {sub.prequalExpiry && <span className="text-xs text-slate-400">Exp {formatDate(sub.prequalExpiry)}</span>}
            </div>
          </div>
          {canReview && sub.status === 'PENDING' && (
            <div className="pt-3 border-t">
              <button className="w-full py-2 text-sm font-medium text-indigo-600 bg-indigo-50 hover:bg-indigo-100 rounded-lg border border-indigo-200 transition-colors">
                Record pre-qualification approval
              </button>
            </div>
          )}
        </div>
      )}

      {/* Tab: SWMS */}
      {tab === 'swms' && (
        <div className="space-y-3">
          {sub.swms.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-xl border border-slate-200">
              <FileText className="w-10 h-10 text-slate-200 mx-auto mb-3" />
              <p className="text-sm text-slate-500">No SWMS submitted yet</p>
              <p className="text-xs text-slate-400 mt-1">Subcontractor must submit a SWMS before commencing work</p>
            </div>
          ) : (
            sub.swms.map(s => {
              const sm  = SWMS_STATUS_META[s.status]
              const job = MOCK_JOBS.find(j => j.id === s.linkedJobId)
              const needsReview = canReview && (s.status === 'SUBMITTED' || s.status === 'UNDER_REVIEW')
              return (
                <div key={s.id} className="bg-white border border-slate-200 rounded-xl p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <span className={`text-xs font-medium px-2.5 py-0.5 rounded-full ${sm.bg} ${sm.text}`}>
                          {sm.label}
                        </span>
                        {job && <span className="text-xs text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded-full">{job.jobNumber}</span>}
                        <span className="text-xs text-slate-400">{s.revision}</span>
                      </div>
                      <p className="text-sm font-medium text-slate-900">{s.title}</p>
                      <p className="text-xs text-slate-400 mt-0.5">
                        Submitted {formatDate(s.submittedAt)} by {s.submittedByName}
                      </p>
                      {s.reviewedByName && (
                        <p className="text-xs text-slate-400 mt-0.5">
                          {s.status === 'APPROVED' ? '✓ Approved' : '✗ Reviewed'} by {s.reviewedByName} · {formatDate(s.reviewedAt!)}
                        </p>
                      )}
                      {s.reviewNotes && (
                        <p className="text-xs text-slate-500 bg-slate-50 rounded px-2 py-1.5 mt-1.5 italic">{s.reviewNotes}</p>
                      )}
                    </div>
                    {needsReview && (
                      <button onClick={() => setReviewingSwms(s)}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-indigo-700 bg-indigo-50 hover:bg-indigo-100 rounded-lg border border-indigo-200 transition-colors flex-shrink-0">
                        <ClipboardList className="w-3.5 h-3.5" /> Review
                      </button>
                    )}
                  </div>
                </div>
              )
            })
          )}
        </div>
      )}

      {/* Tab: Workers */}
      {tab === 'workers' && (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <div className="px-5 py-3 border-b bg-slate-50">
            <p className="text-sm font-medium text-slate-900">Workers from {sub.companyName}</p>
            <p className="text-xs text-slate-400 mt-0.5">Cross-referenced from induction records</p>
          </div>
          {subWorkers.length === 0 ? (
            <div className="text-center py-10 text-slate-400">
              <Users className="w-8 h-8 mx-auto mb-2 text-slate-200" />
              <p className="text-sm">No workers inducted from this company yet</p>
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {subWorkers.map(w => (
                <div key={w.id} className="flex items-center justify-between px-5 py-3">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center text-xs font-bold">
                      {w.workerName.split(' ').map(n => n[0]).join('').slice(0, 2)}
                    </div>
                    <div>
                      <p className="text-sm font-medium text-slate-900">{w.workerName}</p>
                      <p className="text-xs text-slate-400">{w.inductionTitle}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {w.uniqueWorkerId && (
                      <span className="text-xs font-mono text-indigo-700 bg-indigo-50 px-2 py-0.5 rounded-lg">
                        ID: {w.uniqueWorkerId}
                      </span>
                    )}
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium
                      ${w.status === 'PASSED' ? 'bg-green-100 text-green-700' : w.status === 'FAILED' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>
                      {w.status}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
          {/* Site access gate summary */}
          <div className="px-5 py-3 bg-slate-50 border-t text-xs text-slate-500">
            Site access requires: ✓ Company induction PASSED · ✓ SWMS approved · ✓ Valid PTW
          </div>
        </div>
      )}

      {/* Tab: Safety score */}
      {tab === 'score' && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            {[
              { label: 'Incidents',         value: sub.safetyScore.incidents,      color: sub.safetyScore.incidents === 0 ? 'text-green-600' : 'text-red-600',   suffix: '' },
              { label: 'Open issues',        value: sub.safetyScore.openIssues,     color: sub.safetyScore.openIssues === 0 ? 'text-green-600' : 'text-amber-600', suffix: '' },
              { label: 'SWMS compliance',    value: sub.safetyScore.swmsCompliance, color: sub.safetyScore.swmsCompliance >= 100 ? 'text-green-600' : 'text-amber-600', suffix: '%' },
              { label: 'Induction rate',     value: sub.safetyScore.inductionRate,  color: sub.safetyScore.inductionRate === 100 ? 'text-green-600' : 'text-amber-600', suffix: '%' },
              { label: 'Overall rating',     value: sub.safetyScore.avgRating > 0 ? sub.safetyScore.avgRating.toFixed(1) : '—', color: sub.safetyScore.avgRating >= 4 ? 'text-green-600' : sub.safetyScore.avgRating >= 3 ? 'text-amber-600' : 'text-red-600', suffix: sub.safetyScore.avgRating > 0 ? '/5' : '' },
            ].map(s => (
              <div key={s.label} className="bg-white border border-slate-200 rounded-xl p-4">
                <p className="text-xs text-slate-400">{s.label}</p>
                <p className={`text-2xl font-bold mt-1 ${s.color}`}>{s.value}{s.suffix}</p>
              </div>
            ))}
          </div>

          {/* Star rating display */}
          {sub.safetyScore.avgRating > 0 && (
            <div className="bg-white border border-slate-200 rounded-xl p-5">
              <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">Performance rating</p>
              <div className="flex items-center gap-3">
                <div className="flex gap-1">
                  {[1,2,3,4,5].map(i => (
                    <Star key={i} className={`w-6 h-6 ${i <= Math.round(sub.safetyScore.avgRating) ? 'text-amber-400 fill-amber-400' : 'text-slate-200'}`} />
                  ))}
                </div>
                <span className="text-lg font-bold text-slate-700">{sub.safetyScore.avgRating.toFixed(1)}</span>
                <span className="text-sm text-slate-400">out of 5.0</span>
              </div>
              <p className="text-xs text-slate-400 mt-2">Based on quality, safety, program, and communication scores across all engagements</p>
            </div>
          )}

          {sub.safetyScore.avgRating === 0 && (
            <div className="text-center py-10 bg-white rounded-xl border border-slate-200">
              <Star className="w-8 h-8 text-slate-200 mx-auto mb-2" />
              <p className="text-sm text-slate-400">No performance ratings recorded yet</p>
            </div>
          )}
        </div>
      )}

      {/* Review SWMS modal */}
      {reviewingSwms && (
        <ReviewSWMSModal
          swms={reviewingSwms}
          subName={sub.companyName}
          onClose={() => setReviewingSwms(null)}
          onDecision={handleSwmsDecision}
        />
      )}
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────
// MAIN PAGE
// ─────────────────────────────────────────────────────────────────



// ─────────────────────────────────────────────────────────────────
// PHASE 4 — SUBCONTRACTOR NON-COMPLIANCE NOTICE
// Formal written notice of a WHS compliance failure.
// Creates the audit trail required for contract enforcement.
// ─────────────────────────────────────────────────────────────────
function generateNonComplianceNotice(sub: any, form: {
  noticeRef: string; issueDate: string
  nonComplianceType: string; description: string
  contractRef: string; obligationRef: string
  rectificationRequired: string; rectificationDeadline: string
  consequences: string; whsoName: string
}) {
  const now = new Date().toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' })
  const fmtD = (d: string) => d ? new Date(d).toLocaleDateString('en-AU', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }) : '—'
  const NC_TYPES: Record<string,string> = {
    INSURANCE_LAPSED: 'Insurance documentation lapsed / not renewed',
    SWMS_NOT_SUBMITTED: 'SWMS not submitted prior to commencing high-risk work',
    SWMS_NOT_APPROVED: 'Work commenced before SWMS was approved',
    INDUCTION_NOT_COMPLETE: 'Worker commenced on site before completing site induction',
    LICENCE_EXPIRED: 'Work performed by unlicensed or incorrectly licensed person',
    PPE_NON_COMPLIANCE: 'Workers observed without required PPE',
    UNSAFE_WORK_PRACTICE: 'Unsafe work practice observed on site',
    INCIDENT_NOT_REPORTED: 'WHS incident not reported to MCB Civil Electrical WHSO',
    OTHER: 'Other — refer to description',
  }

  const html = `<!DOCTYPE html><html lang="en"><head><meta charset="UTF-8">
<title>Non-Compliance Notice — ${form.noticeRef}</title>
<style>
*{box-sizing:border-box}body{font-family:Arial,sans-serif;font-size:11pt;color:#111;margin:22mm}
h1{font-size:22pt;font-weight:900;color:#1B3A6B;margin:0}
.hdr{border-bottom:3px solid #1B3A6B;padding-bottom:10px;margin-bottom:20px;display:flex;justify-content:space-between;align-items:flex-end}
p{line-height:1.85;margin-bottom:10px}
.nc-banner{background:#fee2e2;border:2px solid #b91c1c;border-radius:5px;padding:12px 16px;margin:14px 0;text-align:center;color:#b91c1c;font-weight:900;font-size:13pt}
.info table{width:100%;border-collapse:collapse;margin-bottom:14px}
.info td{padding:5px 8px;border-bottom:1px solid #f3f4f6}.info td:first-child{width:36%;font-weight:bold;color:#555;background:#f9fafb}
.scope-box{border:1px solid #ccc;border-radius:4px;padding:10px 14px;background:#fafafa;margin-bottom:12px;min-height:40px;white-space:pre-line;line-height:1.7}
.deadline-box{background:#fff7ed;border:1.5px solid #f97316;border-radius:5px;padding:10px 14px;margin:14px 0;text-align:center}
.sig-line{border-bottom:1.5px solid #333;height:32px;margin:22px 0 4px}.sig-lbl{font-size:8pt;color:#888}
.sig-row{display:flex;gap:40px;margin-top:16px}
.sig-block{flex:1}
.footer{font-size:7.5pt;color:#aaa;text-align:center;margin-top:22px;border-top:1px solid #eee;padding-top:7px}
@media print{body{margin:16mm}}
</style></head><body>
<div class="hdr">
  <div><h1>MCB Civil Electrical</h1><div style="font-size:9pt;color:#888">ABN 12 345 678 901 · 14 Commerce Dr, Salisbury QLD 4107 · 07 3344 5500 · whs@mcbce.com.au</div></div>
  <div style="text-align:right"><div style="font-size:9pt;color:#888">Date: ${form.issueDate ? fmtD(form.issueDate) : now}</div><div style="font-size:9pt;color:#888">Notice ref: ${form.noticeRef}</div></div>
</div>

<p><strong>${sub.companyName}</strong><br>
ABN: ${sub.abn ?? '—'}<br>
Attention: ${sub.contactName ?? 'Site Supervisor'}</p>
<br>
<p><strong>WHS NON-COMPLIANCE NOTICE</strong></p>
<p><strong>Subject: Notice of WHS non-compliance — ${form.noticeRef}</strong></p>
<br>

<div class="nc-banner">⚠ NON-COMPLIANCE NOTICE — IMMEDIATE ATTENTION REQUIRED</div>

<p>Dear ${sub.contactName ?? sub.companyName},</p>

<p>MCB Civil Electrical Pty Ltd, as the principal Work Health and Safety duty holder on the above project, hereby issues this formal notice of WHS non-compliance in accordance with our contractual and statutory obligations.</p>

<div class="info"><table>
  <tr><td>Notice reference</td><td><strong>${form.noticeRef}</strong></td></tr>
  <tr><td>Subcontractor</td><td><strong>${sub.companyName}</strong> · ABN ${sub.abn ?? '—'}</td></tr>
  <tr><td>Contract / job reference</td><td>${form.contractRef}</td></tr>
  <tr><td>Non-compliance type</td><td><strong>${NC_TYPES[form.nonComplianceType] ?? form.nonComplianceType}</strong></td></tr>
  <tr><td>Contractual / legal obligation</td><td>${form.obligationRef || 'WHS Act 2011 (Qld) and contract conditions'}</td></tr>
</table></div>

<p><strong>Details of non-compliance:</strong></p>
<div class="scope-box">${form.description || '—'}</div>

<p><strong>Rectification required:</strong></p>
<div class="scope-box">${form.rectificationRequired || '—'}</div>

<div class="deadline-box">
  <div style="font-size:9pt;color:#c2410c;font-weight:bold;margin-bottom:4px">RECTIFICATION DEADLINE</div>
  <div style="font-size:16pt;font-weight:900;color:#c2410c">${form.rectificationDeadline ? fmtD(form.rectificationDeadline) : 'IMMEDIATELY'}</div>
</div>

<p><strong>Consequences of non-rectification:</strong></p>
<p>${form.consequences || 'Failure to rectify this non-compliance by the deadline stated above may result in: (a) removal of the relevant workers from site; (b) suspension of the subcontract; (c) termination of the subcontract for cause; and/or (d) notification to WorkSafe Queensland and relevant licensing authorities.'}</p>

<p>Please acknowledge receipt of this notice by signed return and confirm your plan to rectify the non-compliance by the deadline stated. Acknowledgement does not constitute an admission of liability but confirms you have received and understood this notice.</p>

<div class="sig-row">
  <div class="sig-block">
    <div class="sig-line"></div>
    <div class="sig-lbl">MCB Civil Electrical WHSO / PM — ${form.whsoName || '—'} · date</div>
  </div>
  <div class="sig-block">
    <div style="font-size:9.5pt;font-weight:bold;color:#b91c1c;margin-bottom:4px">Acknowledgement — to be returned to MCB CE</div>
    <div class="sig-line"></div>
    <div class="sig-lbl">${sub.companyName} representative — name, position, signature &amp; date</div>
  </div>
</div>

<div class="footer">MCB Civil Electrical Pty Ltd · ABN 12 345 678 901 · Non-Compliance Notice ${form.noticeRef} · ${now}<br>Retain a signed copy for contract records. Failure to acknowledge does not relieve the subcontractor of its obligations under the notice.</div>
</body></html>`
  const win = window.open('', '_blank', 'width=960,height=820')
  if (!win) { alert('Pop-up blocked — allow pop-ups to print notice'); return }
  win.document.write(html); win.document.close()
  setTimeout(() => { win.focus(); win.print() }, 350)
}


// ─────────────────────────────────────────────────────────────────
// PHASE 5 — SUBCONTRACTOR COMPLIANCE REGISTER (CSV)
// All subcontractors with insurance status, licence, prequalification
// and SWMS status — for WHSO audit and PM review.
// ─────────────────────────────────────────────────────────────────
function exportSubcontractorComplianceCSV(subs: any[]) {
  const fmtD = (d: string | null | undefined) => d ? new Date(d).toLocaleDateString('en-AU') : '—'
  const daysLeft = (d: string | undefined) => {
    if (!d) return null
    return Math.ceil((new Date(d).getTime() - Date.now()) / 86400000)
  }
  const docStatus = (expiry: string | undefined) => {
    const d = daysLeft(expiry)
    if (d === null) return 'Not provided'
    if (d < 0) return 'EXPIRED'
    if (d <= 30) return `Expiring (${d}d)`
    return 'Current'
  }

  const rows: string[][] = [
    ['Company Name','ABN','Trade','Status','Prequalification',
     'PL Insurance — Insurer','PL Insurance — Policy No','PL Insurance — Expiry','PL Status',
     'Workers Comp — Insurer','Workers Comp — Expiry','WC Status',
     'Electrical Licence No','Licence Expiry','Licence Status',
     'WHS Management System','SWMS Submitted','SWMS Status',
     'Safety Score','On-site Jobs','Overall Compliance']
  ]
  for (const s of subs) {
    const overallColor = (s: any) => {
      const pl = daysLeft(s.publicLiability?.expiry)
      const wc = daysLeft(s.workersComp?.expiry)
      const el = daysLeft(s.electricalLicence?.expiry)
      if ([pl,wc,el].some(d => d !== null && d < 0)) return 'NON-COMPLIANT'
      if ([pl,wc,el].some(d => d !== null && d <= 30)) return 'ACTION REQUIRED'
      return 'COMPLIANT'
    }
    const latestSwms = s.swms?.[s.swms.length-1]
    rows.push([
      s.companyName, s.abn ?? '', (s.trade ?? '').replace(/_/g,' '),
      s.status ?? '', s.prequalStatus ?? '',
      s.publicLiability?.insurer ?? '', s.publicLiability?.docNumber ?? '',
      fmtD(s.publicLiability?.expiry), docStatus(s.publicLiability?.expiry),
      s.workersComp?.insurer ?? '', fmtD(s.workersComp?.expiry), docStatus(s.workersComp?.expiry),
      s.electricalLicence?.docNumber ?? '', fmtD(s.electricalLicence?.expiry), docStatus(s.electricalLicence?.expiry),
      s.whsManagementSystem ? 'Yes' : 'No',
      String(s.swms?.length ?? 0),
      latestSwms ? latestSwms.status : 'None submitted',
      s.safetyScore?.avgRating ?? '—',
      (s.onSiteJobs ?? []).join(', '),
      overallColor(s),
    ])
  }
  const csv = rows.map(r => r.map(c => `"${String(c).replace(/"/g,'""')}"`).join(',')).join(String.fromCharCode(10))
  const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8' })
  const url  = URL.createObjectURL(blob)
  const a    = document.createElement('a')
  a.href = url; a.download = `MCB_Subcontractor_Compliance_Register_${new Date().toISOString().slice(0,10)}.csv`
  document.body.appendChild(a); a.click()
  document.body.removeChild(a); URL.revokeObjectURL(url)
}
export function SubcontractorWHS() {
  const { isTier } = useAuthStore()

  const [subs, setSubs]         = useState<Subcontractor[]>(MOCK_SUBCONTRACTORS)

  // Live subcontractor WHS count from Neon
  const { data: liveSubsRes } = useQuery({
    queryKey: ['whs-subcontractors'],
    queryFn:  async () => {
      const { data } = await api.get('/api/whs/subcontractors')
      return data as any
    },
    staleTime: 60_000,
  })
  const liveSubCount: number = (liveSubsRes as any)?.count ?? 0
  const [selected, setSelected] = useState<Subcontractor | null>(null)
  const [tab, setTab]           = useState<'register' | 'swms' | 'alerts'>('register')
  const [search, setSearch]     = useState('')
  const [tradeFilter, setTradeFilter]   = useState<TradeType | 'ALL'>('ALL')
  const [statusFilter, setStatusFilter] = useState<SubStatus | 'ALL'>('ALL')

  const canManage = isTier('B_PC')

  // ── Derived ────────────────────────────────────────────────────
  const stats = useMemo(() => ({
    approved:   subs.filter(s => s.status === 'APPROVED').length,
    pending:    subs.filter(s => s.status === 'PENDING').length,
    swmsPending: subs.flatMap(s => s.swms).filter(s => s.status === 'SUBMITTED' || s.status === 'UNDER_REVIEW').length,
    docAlerts:  subs.filter(s => {
      const ds1 = docStatus(s.publicLiability)
      const ds2 = docStatus(s.workersComp)
      return ds1 === 'EXPIRED' || ds1 === 'EXPIRING' || ds2 === 'EXPIRED' || ds2 === 'EXPIRING'
    }).length,
  }), [subs])

  const filteredSubs = useMemo(() => subs.filter(s => {
    const q = search.toLowerCase()
    const matchSearch = !q || s.companyName.toLowerCase().includes(q) || s.contactName.toLowerCase().includes(q) || s.abn.includes(q)
    const matchTrade  = tradeFilter === 'ALL' || s.trade === tradeFilter
    const matchStatus = statusFilter === 'ALL' || s.status === statusFilter
    return matchSearch && matchTrade && matchStatus
  }), [subs, search, tradeFilter, statusFilter])

  // All SWMS awaiting review across all subs
  const pendingSwmsList = useMemo(() =>
    subs.flatMap(s => s.swms
      .filter(sw => sw.status === 'SUBMITTED' || sw.status === 'UNDER_REVIEW')
      .map(sw => ({ ...sw, subId: s.id, subName: s.companyName }))
    ),
    [subs]
  )

  // All compliance alerts
  const complianceAlerts = useMemo(() =>
    subs.flatMap(s => {
      const alerts: { subName: string; subId: string; doc: string; status: DocStatus; expiry?: string }[] = []
      const check = (doc: ComplianceDoc) => {
        const ds = docStatus(doc)
        if (ds === 'EXPIRED' || ds === 'EXPIRING' || ds === 'MISSING')
          alerts.push({ subName: s.companyName, subId: s.id, doc: doc.label, status: ds, expiry: doc.expiry })
      }
      check(s.publicLiability)
      check(s.workersComp)
      if (s.trade === 'ELECTRICAL') check(s.electricalLicence)
      return alerts
    }),
    [subs]
  )

  const handleSwmsDecision = (subId: string, swmsId: string, decision: 'APPROVED' | 'REJECTED', notes: string) => {
    setSubs(prev => prev.map(s => s.id !== subId ? s : {
      ...s,
      swms: s.swms.map(sw => sw.id !== swmsId ? sw : {
        ...sw, status: decision,
        reviewedByName: 'Sarah Kim (WHSO)',
        reviewedAt: new Date().toISOString(),
        reviewNotes: notes || undefined,
      }),
    }))
    // Also update selected
    if (selected?.id === subId) {
      setSelected(prev => prev ? {
        ...prev,
        swms: prev.swms.map(sw => sw.id !== swmsId ? sw : {
          ...sw, status: decision, reviewedByName: 'Sarah Kim (WHSO)',
          reviewedAt: new Date().toISOString(), reviewNotes: notes || undefined,
        }),
      } : null)
    }
  }

  // Show detail view
  if (selected) {
    return (
      <SubcontractorDetail
        sub={selected}
        onBack={() => setSelected(null)}
        onSwmsDecision={handleSwmsDecision}
      />
    )
  }

  return (
    <div className="p-6 space-y-6">

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Subcontractor WHS</h1>
          <p className="text-sm text-slate-500 mt-0.5">
            Pre-qualification, compliance documents, SWMS review, and site access control
            {liveSubCount > 0 && <span className="ml-2 text-blue-600 font-medium">· {liveSubCount} live from Neon</span>}
          </p>
        </div>
        {canManage && (
          <button className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium rounded-lg transition-colors">
            <Plus className="w-4 h-4" /> Add subcontractor
          </button>
        )}
        <button onClick={() => exportSubcontractorComplianceCSV(subs)} className="flex items-center gap-2 px-3 py-2 border border-slate-200 hover:bg-slate-50 text-slate-700 text-sm font-medium rounded-lg transition-colors">
          <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>
          Compliance register
        </button>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {[
          { label: 'Approved subs',      value: stats.approved,     color: 'text-green-600',  bg: 'bg-green-50',  tabTarget: 'register' as const },
          { label: 'Pending approval',   value: stats.pending,      color: 'text-amber-600',  bg: 'bg-amber-50',  tabTarget: 'register' as const },
          { label: 'SWMS awaiting review',value: stats.swmsPending, color: 'text-blue-600',   bg: 'bg-blue-50',   tabTarget: 'swms' as const },
          { label: 'Compliance alerts',  value: stats.docAlerts,    color: stats.docAlerts > 0 ? 'text-red-600' : 'text-green-600',
            bg: stats.docAlerts > 0 ? 'bg-red-50' : 'bg-green-50', tabTarget: 'alerts' as const },
        ].map(s => (
          <div key={s.label} onClick={() => setTab(s.tabTarget)}
            className={`${s.bg} rounded-xl p-4 cursor-pointer hover:opacity-80 transition-opacity`}>
            <p className="text-xs text-slate-500">{s.label}</p>
            <p className={`text-2xl font-bold mt-1 ${s.color}`}>{s.value}</p>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-slate-100 rounded-xl p-1">
        {[
          { id: 'register', label: `Subcontractors (${subs.length})` },
          { id: 'swms',     label: `SWMS Review (${stats.swmsPending})`, alert: stats.swmsPending > 0 },
          { id: 'alerts',   label: `Compliance Alerts (${complianceAlerts.length})`, alert: complianceAlerts.length > 0 },
        ].map(t => (
          <button key={t.id} onClick={() => setTab(t.id as any)}
            className={`flex-1 flex items-center justify-center gap-1.5 text-sm font-medium py-2 rounded-lg transition-colors relative
              ${tab === t.id ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>
            {t.label}
            {t.alert && <span className="w-2 h-2 rounded-full bg-red-500 flex-shrink-0" />}
          </button>
        ))}
      </div>

      {/* ── TAB: SUBCONTRACTOR REGISTER ─────────────────────── */}
      {tab === 'register' && (
        <div className="space-y-4">
          {/* Filters */}
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input value={search} onChange={e => setSearch(e.target.value)}
                placeholder="Search company name, contact, ABN..."
                className="input pl-9 w-full text-sm" />
            </div>
            <select value={tradeFilter} onChange={e => setTradeFilter(e.target.value as any)} className="input text-sm">
              <option value="ALL">All trades</option>
              {(Object.keys(TRADE_META) as TradeType[]).map(t => (
                <option key={t} value={t}>{TRADE_META[t].icon} {TRADE_META[t].label}</option>
              ))}
            </select>
            <select value={statusFilter} onChange={e => setStatusFilter(e.target.value as any)} className="input text-sm">
              <option value="ALL">All statuses</option>
              {(Object.keys(STATUS_META) as SubStatus[]).map(s => (
                <option key={s} value={s}>{STATUS_META[s].label}</option>
              ))}
            </select>
          </div>

          {/* Sub cards */}
          <div className="space-y-3">
            {filteredSubs.map(sub => {
              const compliance = overallCompliance(sub)
              const tm         = TRADE_META[sub.trade]
              const sm         = STATUS_META[sub.status]
              const pendingCount = sub.swms.filter(s => s.status === 'SUBMITTED' || s.status === 'UNDER_REVIEW').length
              const workers    = MOCK_WORKER_INDUCTIONS.filter(w => w.workerType === 'SUBCONTRACTOR' && w.company === sub.companyName)

              return (
                <div key={sub.id} onClick={() => setSelected(sub)}
                  className="bg-white border border-slate-200 rounded-xl p-4 hover:shadow-md hover:border-indigo-200 cursor-pointer transition-all">
                  <div className="flex items-start gap-3">
                    {/* Compliance dot */}
                    <div className="flex flex-col items-center gap-1 pt-1">
                      <span className={`w-3 h-3 rounded-full flex-shrink-0 ${COMPLIANCE_DOT[compliance]}`} title={`WHS: ${compliance}`} />
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap mb-0.5">
                            <span className="font-semibold text-slate-900 text-sm">{sub.companyName}</span>
                            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${sm.bg} ${sm.text}`}>{sm.label}</span>
                            <span className={`text-xs px-2 py-0.5 rounded-full ${tm.bg} ${tm.text}`}>{tm.icon} {tm.label}</span>
                          </div>
                          <div className="flex items-center gap-4 text-xs text-slate-400">
                            <span className="flex items-center gap-1"><Users className="w-3 h-3" />{sub.contactName}</span>
                            <span>{sub.abn}</span>
                            {sub.onSiteJobs.length > 0 && (
                              <span className="text-indigo-600">
                                {sub.onSiteJobs.map(id => MOCK_JOBS.find(j => j.id === id)?.jobNumber).join(', ')}
                              </span>
                            )}
                          </div>
                        </div>
                        <ChevronRight className="w-4 h-4 text-slate-300 flex-shrink-0 mt-0.5" />
                      </div>

                      {/* Compact doc health row */}
                      <div className="flex items-center gap-3 mt-2.5 flex-wrap">
                        {[
                          { label: 'PL Ins',  ds: docStatus(sub.publicLiability) },
                          { label: 'WC',      ds: docStatus(sub.workersComp) },
                          { label: 'Pre-qual', ds: sub.prequalStatus === 'APPROVED' ? 'CURRENT' as DocStatus : sub.prequalStatus === 'EXPIRED' ? 'EXPIRED' as DocStatus : 'EXPIRING' as DocStatus },
                        ].map(d => {
                          const m = DOC_STATUS_META[d.ds]
                          const DI = m.icon
                          return (
                            <span key={d.label} className={`flex items-center gap-1 text-xs px-2 py-0.5 rounded-full ${m.bg} ${m.text}`}>
                              <DI className="w-3 h-3" />{d.label}
                            </span>
                          )
                        })}
                        {workers.length > 0 && (
                          <span className="flex items-center gap-1 text-xs text-slate-500">
                            <UserCheck className="w-3 h-3" /> {workers.filter(w => w.status === 'PASSED').length}/{workers.length} inducted
                          </span>
                        )}
                        {pendingCount > 0 && (
                          <span className="flex items-center gap-1 text-xs text-blue-700 bg-blue-50 px-2 py-0.5 rounded-full">
                            <Clock className="w-3 h-3" /> {pendingCount} SWMS pending review
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      )}

      {/* ── TAB: SWMS REVIEW ─────────────────────────────── */}
      {tab === 'swms' && (
        <div className="space-y-3">
          {pendingSwmsList.length === 0 ? (
            <div className="text-center py-16 bg-white rounded-xl border border-slate-200">
              <CheckCircle className="w-10 h-10 text-green-300 mx-auto mb-3" />
              <p className="font-medium text-slate-500">No SWMS awaiting review</p>
              <p className="text-sm text-slate-400 mt-1">All submitted SWMS have been reviewed</p>
            </div>
          ) : (
            pendingSwmsList.map(sw => {
              const job = MOCK_JOBS.find(j => j.id === sw.linkedJobId)
              return (
                <div key={`${sw.subId}-${sw.id}`} className="bg-white border border-blue-200 rounded-xl p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <span className={`text-xs font-medium px-2.5 py-0.5 rounded-full ${SWMS_STATUS_META[sw.status].bg} ${SWMS_STATUS_META[sw.status].text}`}>
                          {SWMS_STATUS_META[sw.status].label}
                        </span>
                        <span className="text-xs font-semibold text-slate-700">{sw.subName}</span>
                        {job && <span className="text-xs text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded-full">{job.jobNumber}</span>}
                      </div>
                      <p className="text-sm font-medium text-slate-900">{sw.title}</p>
                      <p className="text-xs text-slate-400 mt-0.5">
                        {sw.revision} · Submitted {formatDate(sw.submittedAt)} by {sw.submittedByName}
                      </p>
                    </div>
                    <button
                      onClick={() => {
                        const sub = subs.find(s => s.id === sw.subId)
                        if (sub) setSelected(sub)
                        setTimeout(() => {}, 0)
                      }}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-indigo-700 bg-indigo-50 hover:bg-indigo-100 rounded-lg border border-indigo-200 transition-colors flex-shrink-0">
                      <ExternalLink className="w-3.5 h-3.5" /> Open &amp; review
                    </button>
                  </div>
                </div>
              )
            })
          )}
        </div>
      )}

      {/* ── TAB: COMPLIANCE ALERTS ───────────────────────── */}
      {tab === 'alerts' && (
        <div className="space-y-3">
          {complianceAlerts.length === 0 ? (
            <div className="text-center py-16 bg-white rounded-xl border border-slate-200">
              <Shield className="w-10 h-10 text-green-300 mx-auto mb-3" />
              <p className="font-medium text-slate-500">All documents current</p>
              <p className="text-sm text-slate-400 mt-1">No compliance alerts at this time</p>
            </div>
          ) : (
            complianceAlerts.map((alert, i) => {
              const dm = DOC_STATUS_META[alert.status]
              const DI = dm.icon
              return (
                <div key={i} className={`bg-white border rounded-xl p-4 flex items-center justify-between gap-3 ${alert.status === 'EXPIRED' ? 'border-red-200' : alert.status === 'MISSING' ? 'border-red-200' : 'border-amber-200'}`}>
                  <div className="flex items-center gap-3">
                    <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${dm.bg}`}>
                      <DI className={`w-5 h-5 ${dm.text}`} />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-slate-900">{alert.subName}</p>
                      <p className="text-xs text-slate-500">{alert.doc}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0">
                    <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${dm.bg} ${dm.text}`}>
                      {dm.label}{alert.expiry ? ` · ${formatDate(alert.expiry)}` : ''}
                    </span>
                    <button onClick={() => setSelected(subs.find(s => s.companyName === alert.subName)!)}
                      className="text-xs text-indigo-600 hover:underline">
                      View
                    </button>
                  </div>
                </div>
              )
            })
          )}
        </div>
      )}
    </div>
  )
}
